import React, { useState, useEffect, useMemo } from 'react';
import { DraggableModal } from 'src/app/components';
import 'src/app/popup.scss';
import styles from 'src/app/pages/Administration/tabs/UserTab/User.module.scss';

import { userService } from 'src/app/services';
import { useDispatch, useSelector } from 'react-redux';
import { reduxRootTypes } from 'src/types';
import { userActions } from 'src/app/redux/actions';
import { errorLocals } from 'src/locals';
import { statusHelper } from 'src/app/helpers';
import { RegisterDataType, EventType, EventTypeInForm } from 'src/types/services/user-service.type';
import { UserFormType } from './userForm.config';

import { v4 } from 'uuid';
import { DeleteOutlined } from '@ant-design/icons';
import PasswordChangeForm from '../PasswordChangeForm/PasswordChangeForm';
import UserMailServerCredentialsForm from '../UserMailServerCredentialsForm';
import { useTranslation } from 'react-i18next';
import { isCurrentUserAdminInDatabase } from 'src/app/helpers/isUserOperationsDisabled.helper';

import Input from 'antd/es/input';
import Form from 'antd/es/form';
import Button from 'antd/es/button';
import Select from 'antd/es/select';
import Collapse from 'antd/es/collapse';

const UserForm: React.FC<UserFormType> = ({ onClickOpen, setOnClickOpen, setEditModal, userForm, editProfile }) => {
  const { t } = useTranslation();

  const defaultUserData = {
    id: '',
    userName: '',
    name: undefined,
    surname: undefined,
    email: undefined,
    telephone: undefined,
    organization: undefined,
    password: '',
    passwordConfirmation: '',
    // userRoleForEvent: [],
    events: [],
    // userRoles: [],
    sessionTime: undefined,
    isLocalUser: true,
  };

  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState<number>(0);

  const [addUserModal, setAddUserModal] = useState<boolean>(false);
  const [modalInEditMode, setModalInEditMode] = useState<boolean>(false);

  const [form] = Form.useForm();
  const [userDataInForm, setUserDataInForm] = useState<RegisterDataType>(userForm || defaultUserData);
  const [selectedEvents, setSelectedEvents] = useState<string[]>(userDataInForm.events.map((item) => item.id));
  const [changePasswordModal, setChangePasswordModal] = useState<boolean>(false);
  const [mailServerModal, setMailServerModal] = useState<boolean>(false);
  const [isRequiredInputDisabled, setIsRequiredInputDisabled] = useState(false);
  const [isInputDisabled, setIsInputDisabled] = useState(false);

  const { eventList } = useSelector((state: reduxRootTypes.RootStateType) => state.eventState);
  const userState = useSelector((state: reduxRootTypes.RootStateType) => state.userState);

  const { userrolepermissions } = useSelector((state: reduxRootTypes.RootStateType) => state.userRolePermissionState);
  const { ontologies } = useSelector((state: reduxRootTypes.RootStateType) => state.ontologyState);

  const currentUserAdminEvents = useMemo(() => {
    return userState.events.filter((e) => e.roles.some((r) => r.abbreviation === 'Admin'));
  }, [userState, eventList]);

  type FilterEventList =
    | {
        value: string;
        option: string;
      }
    | undefined;
  const defaultFilteredEventData = eventList.map((item) => {
    if (!selectedEvents.includes(item.id)) {
      return {
        key: item.id,
        value: item.id,
        option: item.eventId,
      };
    }
  });
  const [filteredEventList, setFilteredEventList] = useState<FilterEventList[]>(defaultFilteredEventData);

  const onFormChange = (inputLabel: keyof RegisterDataType, value: any) => {
    setUserDataInForm({
      ...userDataInForm,
      [inputLabel]: value,
    });
  };

  const onFormSave = async () => {
    if (editProfile) {
      try {
        const userUpdateData = await userService.updateMeProfile({
          ...userDataInForm,
          password: userDataInForm.password ? userDataInForm.password : undefined,
          passwordConfirmation: userDataInForm.passwordConfirmation ? userDataInForm.passwordConfirmation : undefined,
        });

        if (userUpdateData.status === 401) {
          statusHelper.displayErrorMessage(userUpdateData.statusText);
          return;
        }

        if (userUpdateData.status === 400) {
          statusHelper.displayErrorMessage(userUpdateData.statusText);
          return;
        }

        statusHelper.displaySuccessStatusMessage(t('success.updated'));

        dispatch(userActions.setUserEdited(userUpdateData.data));
        dispatch(userActions.setInformation(userUpdateData.data));
        setOnClickOpen(false);
        setUserDataInForm(defaultUserData);
      } catch (error: any) {
        if (error !== errorLocals.errorHandledError) {
          statusHelper.displayErrorMessage(error.response.data.message);
        }
      }
      return;
    }

    if (userDataInForm.events.length === 0) {
      statusHelper.displayErrorMessage(t('errors.userEventEmpty'));
      return;
    } else {
      if (userDataInForm.events.some((obj) => obj.id === '' || obj.roles.length === 0)) {
        statusHelper.displayErrorMessage(t('errors.userEventEmpty'));
        return;
      }
    }
    if (setEditModal) {
      try {
        const userUpdateData = await userService.updateUsers({
          ...userDataInForm,
          password: userDataInForm.password ? userDataInForm.password : undefined,
          passwordConfirmation: userDataInForm.passwordConfirmation ? userDataInForm.passwordConfirmation : undefined,
        });

        setAddUserModal(false);
        setUserDataInForm(defaultUserData);
        dispatch(userActions.setUserEdited(userUpdateData.data));

        if (userDataInForm.id === userState.id) {
          dispatch(userActions.setInformation(userUpdateData.data));
        }

        statusHelper.displaySuccessStatusMessage(t('success.edited'));
        setOnClickOpen(false);
      } catch (error: unknown) {
        statusHelper.displayGenericError(error);
      }
    } else {
      try {
        const usersAddData = await userService.createUser(userDataInForm, {
          blockErrorHandler: false,
        });

        setAddUserModal(false);
        setUserDataInForm(defaultUserData);
        dispatch(userActions.setAddInformation(usersAddData.data.user));
        if (userDataInForm.id === userState.id) {
          dispatch(userActions.setInformation(usersAddData.data.user));
        }
        statusHelper.displaySuccessStatusMessage(t('success.added'));
        setOnClickOpen(false);
      } catch (error) {
        if (error !== errorLocals.errorHandledError) {
          // @ts-ignore
          statusHelper.displayErrorMessage(error.response.data.message);
          setAddUserModal(false);
          setUserDataInForm(defaultUserData);
        }
      }
    }

    setModalInEditMode(false);
  };

  const handleOnClickNext = () => {
    if (currentStep === 1) {
      setCurrentStep(0);
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  function upsert(array: EventTypeInForm[], item: EventTypeInForm) {
    // if (array.length === 4) return array;

    const i = array.findIndex((_item) => _item.formId === item.formId);
    // eslint-disable-next-line no-param-reassign
    if (i > -1) array[i] = item;
    else array.unshift(item);
    return array;
  }

  const onAddEventAndRole = () => {
    setUserDataInForm({
      ...userDataInForm,
      events: upsert(userDataInForm.events || [], {
        formId: v4(),
        roles: [],
        id: '',
      }),
    });
  };

  useEffect(() => {
    const newEventList: FilterEventList[] = [];

    const events =
      userState.userName === 'Admin'
        ? eventList
        : userState.events.filter((e) => e.roles.some((r) => r.abbreviation === 'Admin'));

    events.map((o) => {
      if (!selectedEvents.includes(o.id)) {
        newEventList.push({
          value: o.id,
          option: o.eventId,
        });
      }
    });

    setFilteredEventList(newEventList);
  }, [selectedEvents]);

  const onRoleForEventDetailsChange = (inputLabel: keyof EventType, value: any, formId?: string) => {
    if (userDataInForm.events) {
      const item = userDataInForm.events.find((o: any) => o.formId === formId);
      const index = userDataInForm.events.findIndex((o: any) => o.formId === formId);

      if (item) {
        setUserDataInForm({
          ...userDataInForm,
          events: [
            ...userDataInForm.events.slice(0, index),
            {
              ...item,
              [inputLabel]: value,
            },
            ...userDataInForm.events.slice(index + 1),
          ],
        });

        if (inputLabel === 'id') {
          const newEvent = eventList.find((o) => o.id === value);
          if (newEvent) {
            if (item.id) {
              setSelectedEvents([
                // ...selectedEvents.filter((e) => e !== prev[selectedEvents.length - 1]),
                ...selectedEvents.slice(0, selectedEvents.length - 1),
                newEvent?.id,
              ]);
            } else {
              setSelectedEvents((prev) => [...prev, newEvent?.id]);
            }
          }
        }
      }
    }
  };

  const onRemoveTimeDetails = (formId: string, eventId: string) => {
    setUserDataInForm({
      ...userDataInForm,
      events: userDataInForm?.events.filter((o: any) => o.formId !== formId),
    });
    const deletedEvent = eventList.find((o) => o.id === eventId);
    if (deletedEvent) setSelectedEvents((prev) => prev.filter((e) => e !== deletedEvent.id));
  };

  // System login users are not local
  const editProfileInfo = userState.isLocalUser ? t('userProfileMenu.editProfile') : t('userProfileMenu.profile');

  useEffect(() => {
    if (setEditModal) {
      setIsRequiredInputDisabled(false);
      setIsInputDisabled(false);
    } else if (!setEditModal && !editProfile) {
      setIsRequiredInputDisabled(false);
      setIsInputDisabled(false);
    } else if (editProfile) {
      if (userState.isLocalUser) {
        setIsRequiredInputDisabled(true);
        setIsInputDisabled(false);
      } else {
        setIsRequiredInputDisabled(true);
        setIsInputDisabled(true);
      }
    }
  }, [setEditModal, editProfile]);

  return (
    <>
      {onClickOpen && (
        <DraggableModal
          headerText={
            setEditModal
              ? t('administration.users.editUser')
              : editProfile
              ? editProfileInfo
              : t('administration.users.addUser')
          }
          onClickClose={() => setOnClickOpen(false)}
          style={{ width: '800px', height: '600px' }}
        >
          <Form
            initialValues={userDataInForm}
            onFinish={onFormSave}
            form={form}
            scrollToFirstError={{ behavior: 'smooth' }}
            requiredMark="optional"
            id={styles.userForm}
            autoComplete="off"
          >
            <div className="popup-main-container">
              <div className="popup-container">
                <div className="popup-content" style={{ padding: 0 }}>
                  <div
                    className={styles.container}
                    style={{ paddingLeft: 10, paddingRight: 10, marginRight: 15, marginLeft: 15 }}
                  >
                    <div className="popup-row grid-col-2-fixed">
                      <div className="popup-input-container">
                        <span className="popup-input-title">
                          {t('administration.users.username')} <span className={styles.requiredLabel} />
                        </span>
                        <Form.Item name="userName" rules={[{ required: true, message: t('general.required') || '' }]}>
                          <Input
                            disabled={isRequiredInputDisabled}
                            className={styles.standard_input}
                            value={userDataInForm.userName}
                            onChange={(e) => onFormChange('userName', e.target.value)}
                          />
                        </Form.Item>
                      </div>
                      <div className="popup-input-container">
                        <span className="popup-input-title"> {t('administration.users.name')}</span>
                        <Form.Item name="name">
                          <Input
                            className={styles.standard_input}
                            value={userDataInForm.name}
                            onChange={(e) => onFormChange('name', e.target.value)}
                            disabled={isInputDisabled}
                          />
                        </Form.Item>
                      </div>
                      <div className="popup-input-container">
                        <span className="popup-input-title"> {t('administration.users.surname')}</span>
                        <Form.Item name="surname">
                          <Input
                            className={styles.standard_input}
                            value={userDataInForm.surname}
                            onChange={(e) => onFormChange('surname', e.target.value)}
                            disabled={isInputDisabled}
                          />
                        </Form.Item>
                      </div>
                      <div className="popup-input-container">
                        <span className="popup-input-title">{t('administration.ontologies.email')}</span>
                        <Form.Item
                          name="email"
                          validateTrigger="onBlur"
                          rules={[{ type: 'email', message: t('errors.email') || '' }]}
                        >
                          <Input
                            className={styles.standard_input}
                            value={userDataInForm.email}
                            onChange={(e) => onFormChange('email', e.target.value)}
                            disabled={isInputDisabled}
                          />
                        </Form.Item>
                      </div>

                      <div className="popup-input-container">
                        <span className="popup-input-title">{t('administration.users.telephone')}</span>
                        <Form.Item name="telephone">
                          <Input
                            className={styles.standard_input}
                            value={userDataInForm.telephone}
                            onChange={(e) => onFormChange('telephone', e.target.value)}
                            disabled={isInputDisabled}
                          />
                        </Form.Item>
                      </div>
                      <div className="popup-input-container">
                        <span className="popup-input-title">{t('administration.users.organization')}</span>
                        <Form.Item name="organization">
                          <Input
                            disabled={isInputDisabled}
                            className={styles.standard_input}
                            value={userDataInForm.organization}
                            onChange={(e) => onFormChange('organization', e.target.value)}
                          />
                        </Form.Item>
                      </div>
                      {editProfile && userState.isLocalUser && (
                        <div className="popup-input-container">
                          <span className="popup-input-title">{t('administration.users.currentPassword')}</span>
                          <Form.Item
                            name="password"
                            dependencies={['passwordConfirmation']}
                            rules={[
                              {
                                required: setEditModal ? false : true,
                                message: t('general.required') || '',
                              },
                            ]}
                          >
                            <Input.Password
                              type="password"
                              className={styles.standard_input}
                              // disabled={setEditModal}
                              onWheel={(e) => e.currentTarget.blur()}
                              // value={userDataInForm.password}
                              onChange={(e) => onFormChange('password', e.target.value)}
                            />
                          </Form.Item>
                        </div>
                      )}

                      {/* {editProfile && (
                        <div className="popup-input-container">
                          <span className="popup-input-title">OLD PASSWORD</span>
                          <Form.Item
                            name="oldPassword"
                            rules={[
                              {
                                required: true,
                                message: t('general.required') || "",
                              },
                            ]}
                          >
                            <Input.Password
                              type="password"
                              className={styles.standard_input}
                              onWheel={(e) => e.currentTarget.blur()}
                              onChange={(e) => onFormChange('oldPassword', e.target.value)}
                            />
                          </Form.Item>
                        </div>
                      )} */}

                      {!editProfile && (
                        <>
                          <div className="popup-input-container">
                            <span className="popup-input-title">
                              {t('administration.users.password')}{' '}
                              {!setEditModal && <span className={styles.requiredLabel} />}
                            </span>
                            <Form.Item
                              name="password"
                              dependencies={['passwordConfirmation']}
                              rules={[
                                {
                                  required: setEditModal ? false : true,
                                  message: t('general.required') || '',
                                },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const pass = getFieldValue('passwordConfirmation');
                                    if (pass === value || (!value && !pass)) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('The two passwords do not match!'));
                                  },
                                }),
                              ]}
                            >
                              <Input.Password
                                type="password"
                                className={styles.standard_input}
                                // disabled={setEditModal}
                                onWheel={(e) => e.currentTarget.blur()}
                                // value={userDataInForm.password}
                                onChange={(e) => onFormChange('password', e.target.value)}
                              />
                            </Form.Item>
                          </div>
                          <div className="popup-input-container">
                            <span className="popup-input-title">
                              {t('administration.users.passwordConfirmation')}{' '}
                              {!setEditModal && <span className={styles.requiredLabel} />}
                            </span>
                            <Form.Item
                              name="passwordConfirmation"
                              rules={[
                                {
                                  required: setEditModal ? false : true,
                                  message: t('general.required') || '',
                                },
                              ]}
                            >
                              <Input.Password
                                type="password"
                                className={styles.standard_input}
                                // disabled={setEditModal}
                                onWheel={(e) => e.currentTarget.blur()}
                                // value={userDataInForm.passwordConfirmation}
                                onChange={(e) => onFormChange('passwordConfirmation', e.target.value)}
                              />
                            </Form.Item>
                          </div>
                        </>
                      )}
                      {/* <div className="popup-input-container-private">
                        <span className="popup-input-title">SELECT EVENT</span>
                        <Form.Item name="event" rules={[{ required: true, message: t('general.required') || '' }]}>
                          <Select
                            showSearch
                            showArrow
                            // mode="multiple"
                            className={styles.select}
                            placeholder="Select a event"
                            optionFilterProp="children"
                            onSearch={onSearch}
                            //defaultValue={userDataInForm.event.event}
                            defaultValue={'test'}
                            onChange={(e) => onFormChange('event', e as string, 'event')}
                          >
                            {eventList.map((option) => (
                              <option value={option.id}>{option.eventId}</option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div> */}

                      {/* <div className="popup-input-container-private">
                        <span className="popup-input-title">SELECT USER ROLE</span>
                        <Form.Item name="userRole" rules={[{ required: true, message: t('general.required') || '' }]}>
                          <Select
                            showSearch
                            showArrow
                            mode="multiple"
                            className={styles.select}
                            placeholder="Select a role"
                            optionFilterProp="children"
                            onSearch={onSearch}
                            //defaultValue={userDataInForm.event.userRoles}
                            defaultValue={['test']}
                            onChange={(e) => onFormChange('event', e as string[], 'userRoles')}
                          >
                            {ontologies
                              .filter((obj) => Number(obj.ontologyType) === OntologyEnum.User_Role)
                              .map((option) => (
                                <option value={option.id}>{option.abbreviation}</option>
                              ))}
                          </Select>
                        </Form.Item>
                      </div> */}
                    </div>
                    {editProfile && userState.isLocalUser && (
                      <div className="popup-input-container">
                        <Button className="change-password-button" onClick={() => setChangePasswordModal(true)}>
                          {t('administration.users.changePassword')}
                        </Button>
                      </div>
                    )}
                    <div className="popup-input-container">
                      <Button className="change-password-button" onClick={() => setMailServerModal(true)}>
                        {t('administration.users.enterMailServerCredentials')}
                      </Button>
                    </div>
                    <Collapse defaultActiveKey={['1']}>
                      <Collapse.Panel
                        header={t('administration.users.rolesForDatabase')}
                        key="1"
                        extra={
                          !editProfile && (
                            <div
                              className="gerekli-add"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onAddEventAndRole) onAddEventAndRole();
                              }}
                            >
                              <img src="/assets/collapse-add-icon.png" alt="add" />
                            </div>
                          )
                        }
                      >
                        <div className="popup-row grid-col-2-fixed">
                          {userDataInForm.events.map((roleForEventItems: EventTypeInForm, idx) => (
                            <div key={roleForEventItems.formId}>
                              <div className="popup-input-container-private">
                                <span className="popup-input-title">{t('administration.users.selectDatabase')}</span>
                                <Form.Item
                                  name={`events${roleForEventItems.formId}`}
                                  // name={['events', idx, 'eventId']}
                                  rules={[{ required: false, message: t('general.required') || '' }]}
                                >
                                  <Select
                                    disabled={
                                      roleForEventItems.id === ''
                                        ? false
                                        : editProfile ||
                                          !isCurrentUserAdminInDatabase(
                                            userState,
                                            currentUserAdminEvents,
                                            roleForEventItems.id,
                                          )
                                    }
                                    showSearch
                                    showArrow
                                    // mode="multiple"
                                    className={styles.select}
                                    optionFilterProp="children"
                                    defaultValue={
                                      eventList.find((e) => e.id === roleForEventItems.id)?.eventId || undefined
                                    }
                                    onChange={(e) => onRoleForEventDetailsChange('id', e, roleForEventItems.formId)}
                                  >
                                    {filteredEventList.map(
                                      (option) =>
                                        option && <Select.Option value={option.value}>{option.option}</Select.Option>,
                                    )}
                                  </Select>
                                </Form.Item>
                              </div>
                              <div className="popup-input-container-private">
                                <span className="popup-input-title">{t('administration.users.selectUserRole')}</span>
                                <Form.Item
                                  name={`roles${roleForEventItems.formId}`}
                                  rules={[{ required: false, message: t('general.required') || '' }]}
                                >
                                  <Select
                                    disabled={
                                      roleForEventItems.id === ''
                                        ? false
                                        : editProfile ||
                                          !isCurrentUserAdminInDatabase(
                                            userState,
                                            currentUserAdminEvents,
                                            roleForEventItems.id,
                                          )
                                    }
                                    showSearch
                                    showArrow
                                    mode="multiple"
                                    className={styles.select}
                                    optionFilterProp="children"
                                    maxTagCount="responsive"
                                    defaultValue={roleForEventItems.roles || undefined}
                                    onChange={(e) => onRoleForEventDetailsChange('roles', e, roleForEventItems.formId!)}
                                  >
                                    {ontologies
                                      .filter((o) => {
                                        return (
                                          userrolepermissions.some((role) => role.userRoleId === o.id) &&
                                          o.abbreviation !== 'Root'
                                        );
                                      })
                                      .map((option) => (
                                        <option key={option.id} value={option.id}>
                                          {option.abbreviation}
                                        </option>
                                      ))}
                                  </Select>
                                </Form.Item>
                              </div>
                              {(roleForEventItems.id === '' ||
                                (!editProfile &&
                                  isCurrentUserAdminInDatabase(
                                    userState,
                                    currentUserAdminEvents,
                                    roleForEventItems.id,
                                  ))) && (
                                <div className="popup-input-container">
                                  <Button
                                    className="gerekli-remove"
                                    icon={<DeleteOutlined />}
                                    onClick={() => {
                                      // onRemoveTimeDetails(roleForEventItems.formId!, roleForEventItems.id);
                                      onRemoveTimeDetails(roleForEventItems.formId!, roleForEventItems.id);
                                    }}
                                  >
                                    {t('general.remove')}
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </Collapse.Panel>
                    </Collapse>
                  </div>
                </div>
              </div>
            </div>

            <div className="popup-footer" style={{ marginRight: '6%' }}>
              <div>
                {/* {currentStep !== 1 && (
                  <button className="next-button" onClick={handleOnClickNext} type="button">
                    NEXT
                  </button>
                )} */}

                {!isInputDisabled && (
                  <button className="next-button" onClick={() => setCurrentStep(0)} type="submit">
                    {t('general.save')}
                  </button>
                )}
                <button className="modal-button" onClick={() => setOnClickOpen(false)} type="button">
                  {t('general.cancel')}
                </button>
              </div>
            </div>
          </Form>
        </DraggableModal>
      )}
      {changePasswordModal && (
        <PasswordChangeForm
          isModalOpen={changePasswordModal}
          setIsModalOpen={setChangePasswordModal}
          setOnClickProfileOpen={setOnClickOpen}
        />
      )}
      {mailServerModal && (
        <UserMailServerCredentialsForm
          isModalOpen={mailServerModal}
          setIsModalOpen={setMailServerModal}
          setOnClickMailServerCredentialsOpen={setOnClickOpen}
          userDataInForm={userDataInForm}
        />
      )}
    </>
  );
};

export default UserForm;
